/*
 * moduleRegistry.js — single source of truth for all workspace modules.
 *
 * Adding a new module requires exactly one entry here. Consumers derive
 * everything else (nav groups, labels, package checkboxes, settings
 * toggles) from this registry.
 *
 * What belongs here:
 *   - Module keys (the string used everywhere as identifier)
 *   - Nav group assignment (COMMAND / FINANCE / INVESTMENTS /
 *     OPERATIONS / PRODUCTIVITY) — drives both desktop sidebar
 *     grouping and mobile bottom-nav tab grouping
 *   - English + Arabic fallback labels (used when the super admin
 *     hasn't customized `platformModuleNames`)
 *   - AI support flag (whether the AI assistant per-module toggle
 *     should appear in Settings → Branding → Modules)
 *   - `order` within the group (sidebar + nav row ordering)
 *
 * What does NOT belong here:
 *   - Icons (SVG components live in their UI file since some are in
 *     the shared Ico library, others are inline custom SVGs)
 *   - Badge counts (derived from live workspace state per-module)
 *   - Render components (each module is imported where it's rendered)
 *
 * Rule of thumb: if adding the next module would require touching
 * more than this file + its new feature folder, that's a bug.
 */

export const MODULE_REGISTRY = {
  /* ── COMMAND ── */
  Dashboard:    { group: "COMMAND",     label: "Dashboard",   labelAr: "لوحة التحكم",   ai: true,  order: 0 },
  Execution:    { group: "COMMAND",     label: "Execution",   labelAr: "التنفيذ",       ai: true,  order: 1 },
  Projects:     { group: "COMMAND",     label: "Projects",    labelAr: "المشاريع",      ai: true,  order: 2 },
  SmartHome:    { group: "COMMAND",     label: "Smart Home",  labelAr: "المنزل الذكي",  ai: false, order: 3 },

  /* ── FINANCE ── */
  Income:       { group: "FINANCE",     label: "Income",      labelAr: "الدخل",         ai: true,  order: 0 },
  Expenses:     { group: "FINANCE",     label: "Expenses",    labelAr: "المصروفات",     ai: true,  order: 1 },
  Reports:      { group: "FINANCE",     label: "Reports",     labelAr: "التقارير",      ai: true,  order: 2 },
  Investments:  { group: "FINANCE",     label: "Investments", labelAr: "الاستثمارات",   ai: true,  order: 3 },

  /* ── OPERATIONS ── */
  Household:    { group: "OPERATIONS",  label: "Household",       labelAr: "المنزل",        ai: true,  order: 0 },
  Facility:     { group: "OPERATIONS",  label: "Facility",        labelAr: "إدارة المرافق", ai: true,  order: 1 },
  Fleet:        { group: "OPERATIONS",  label: "Fleet",           labelAr: "الأسطول",       ai: true,  order: 2 },
  Staff:        { group: "OPERATIONS",  label: "Staff",           labelAr: "الموظفون",      ai: true,  order: 3 },
  Transport:    { group: "OPERATIONS",  label: "Transportation",  labelAr: "النقل",         ai: true,  order: 4 },
  Menu:         { group: "OPERATIONS",  label: "Menu Planning",   labelAr: "قائمة الطعام",  ai: true,  order: 5 },
  Suppliers:    { group: "OPERATIONS",  label: "Suppliers",       labelAr: "الموردون",      ai: true,  order: 6 },

  /* ── PRODUCTIVITY ── */
  AIWorkspace:  { group: "PRODUCTIVITY",label: "AI Workspace",    labelAr: "مساحة الذكاء",  ai: false, order: 0 },
  Files:        { group: "PRODUCTIVITY",label: "Files",           labelAr: "الملفات",       ai: false, order: 1 },
  Calendar:     { group: "PRODUCTIVITY",label: "Calendar",        labelAr: "التقويم",       ai: false, order: 2 },
};

/* Group ordering and display labels — drives sidebar section headers */
export const MODULE_GROUP_ORDER = ["COMMAND", "FINANCE", "OPERATIONS", "PRODUCTIVITY"];

/* ── Derived helpers ────────────────────────────────────── */

/* Flat list of all module keys, ordered by group then by order-within-group.
   Used as the canonical list for package editors, nav ordering, etc. */
export const ALL_MODULE_KEYS = (() => {
  const keys = Object.keys(MODULE_REGISTRY);
  return keys.sort((a, b) => {
    const ra = MODULE_REGISTRY[a];
    const rb = MODULE_REGISTRY[b];
    const ga = MODULE_GROUP_ORDER.indexOf(ra.group);
    const gb = MODULE_GROUP_ORDER.indexOf(rb.group);
    if (ga !== gb) return ga - gb;
    return (ra.order || 0) - (rb.order || 0);
  });
})();

/* { Dashboard: "COMMAND", Execution: "COMMAND", ... } — matches the
   old MODULE_GROUPS constant shape for drop-in use in WorkspaceApp. */
export const MODULE_GROUPS = Object.fromEntries(
  ALL_MODULE_KEYS.map(k => [k, MODULE_REGISTRY[k].group])
);

/* List of module keys in a given group, ordered */
export function modulesInGroup(groupKey) {
  return ALL_MODULE_KEYS.filter(k => MODULE_REGISTRY[k].group === groupKey);
}

/* Label resolver with platform override + locale awareness.
   opts: { pmn: platformModuleNames map, isRTL: boolean, fallback: string }
   Super admin's pmn[key] wins over default label in all cases. */
export function moduleLabel(key, opts = {}) {
  const pmn = opts.pmn || {};
  const def = MODULE_REGISTRY[key];
  const override = pmn[key];
  /* In Arabic, only honour a pmn override if it's a real customization —
     admins often leave the input blank or retype the default English label,
     which shouldn't stomp the Arabic registry label. */
  if (opts.isRTL && def) {
    const isGenuineOverride = override && override !== def.label && override !== def.labelAr;
    if (isGenuineOverride) return override;
    if (def.labelAr) return def.labelAr;
    return def.label;
  }
  if (override) return override;
  if (!def) return opts.fallback || key;
  return def.label;
}

/* Set of modules that show an AI per-module toggle in Settings */
export const AI_SUPPORTED_MODULES = new Set(
  ALL_MODULE_KEYS.filter(k => MODULE_REGISTRY[k].ai)
);

/* Build a flat { Dashboard: "Dashboard", ... } map for legacy fallbacks */
export function buildLabelMap(isRTL = false) {
  return Object.fromEntries(
    ALL_MODULE_KEYS.map(k => [k, isRTL && MODULE_REGISTRY[k].labelAr ? MODULE_REGISTRY[k].labelAr : MODULE_REGISTRY[k].label])
  );
}
